import { maxBy, some, uniq } from 'lodash';
import moment from 'moment';

import pendo, { ENTERPRISE_API_KEY } from 'js/lib/pendo';
import user from 'js/lib/user';

import { wasFreeTrialContract } from 'bundles/enterprise-admin-contracts/utils/AdminContractUtils';
import enterpriseAdminUser from 'bundles/enterprise-admin/enterprise-admin-index';
import type { ThirdPartyOrganization } from 'bundles/enterprise-admin/types';
import { DISABLE_PENDO_ORGS } from 'bundles/enterprise-legacy-learner-home/constants/ProgramHomeConstants';

export const initializePendo = ({
  contracts,
  contractTags,
  thirdPartyOrganization,
  isSkillSetsEnabled,
  isCareerAcademySelfServeEnabled,
  roles,
}: {
  contracts: Array<{ contractType: string; endedAt?: number; endsAt?: number }>;
  contractTags: Array<string>;
  thirdPartyOrganization: ThirdPartyOrganization;
  isSkillSetsEnabled?: boolean;
  isCareerAcademySelfServeEnabled?: boolean;
  roles: Array<string>;
}) => {
  const isStandardContract =
    contracts.length === 0 || contracts.some((contract) => contract.contractType === 'STANDARD');

  const { id, name, isReal, organizationType, slug } = thirdPartyOrganization;

  // Init Pendo
  if (user.isAuthenticatedUser() && !DISABLE_PENDO_ORGS.includes(id)) {
    const { external_id: externalId, is_superuser: isSuperuser } = user.get();
    const { outsourcingPermissions, isGwGPaidOrg } = enterpriseAdminUser;

    let pageWidth = 0;
    let pageHeight = 0;
    if (typeof window !== 'undefined') {
      pageWidth = window.innerWidth;
      pageHeight = window.innerHeight;
    }

    const activeContracts = contracts.filter((contract) => !contract.endedAt);
    const maxContractEndsAtDateTS = maxBy(activeContracts, (contract) => contract.endsAt)?.endsAt;
    const maxContractEndsAtDate = maxContractEndsAtDateTS ? moment(maxContractEndsAtDateTS).toISOString() : '';
    const isCourserian = user.isCourserian();
    const email = user.get().email_address;
    const uniqueContractTags = uniq(contractTags) as Array<string>;
    const wasFreeTrial = some(activeContracts, wasFreeTrialContract);
    pendo.init(
      {
        visitor: {
          id: externalId,
          isSuperuser,
          outsourcingPermissions,
          isLoggedIn: true,
          pageWidth,
          pageHeight,
          isCourserian,
          // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          role: roles[0],
          email,
        },
        account: {
          id,
          name,
          slug,
          isRealOrganization: isReal,
          isStandardContract,
          maxContractEndsAtDate,
          contractTags: uniqueContractTags,
          wasFreeTrial,
          organizationType: organizationType || 'undefined',
          isSkillSetsEnabled: isSkillSetsEnabled ?? false,
          isCareerAcademySelfServeEnabled: isCareerAcademySelfServeEnabled ?? false,
          isGwGPaidOrg,
        },
      },
      ENTERPRISE_API_KEY
    );
  }
};
